import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, Area
} from 'recharts';
import '../css/WidgetEnergyCompare.css';
import api from './api';

/**
 * Widget that compares total building energy usage between energy years
 * Shows cumulative usage lines for better progress visualization
 * Improved for mobile display with responsive design
 */
const WidgetEnergyCompare = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [totalComparison, setTotalComparison] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Get current energy year for reference
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const currentEnergyYear = currentMonth >= 7 ? 
    `${currentYear}-${currentYear+1}` : 
    `${currentYear-1}-${currentYear}`;
  const previousEnergyYear = `${parseInt(currentEnergyYear.split('-')[0]) - 1}-${parseInt(currentEnergyYear.split('-')[1]) - 1}`;
  
  // Calculate percentage change between values
  const calculatePercentChange = (current, previous) => {
    if (!previous || previous === 0) return '∞';
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };
  
  // Listen for window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch energy usage data
      const usageResponse = await api.get('/api/kenter/usage');
      
      // Fetch degree days efficiency data
      const efficiencyResponse = await api.get('/api/degree-days/efficiency');
      
      // Fetch last update info
      const updateResponse = await api.get('/api/kenter/last-update');
      
      // Process the data
      processData(usageResponse.data, efficiencyResponse.data);
      
      if (updateResponse.data && updateResponse.data.lastUpdate) {
        setLastUpdate(new Date(updateResponse.data.lastUpdate).toLocaleString('nl-NL'));
      }
      
    } catch (err) {
      console.error('❌ Error fetching energy data:', err);
      setError('Kon de energiegegevens niet laden. Probeer het later nog eens.');
    } finally {
      setLoading(false);
    }
  };
  
  const processData = (usageData, efficiencyData) => {
    // Process monthly data for current and previous year
    const currentYearData = usageData[currentEnergyYear]?.monthly || {};
    const previousYearData = usageData[previousEnergyYear]?.monthly || {};
    
    // Define month order for energy year (July to June)
    const monthOrder = [
      '07', '08', '09', '10', '11', '12', 
      '01', '02', '03', '04', '05', '06'
    ];
    
    // Seasonality map for prediction calculations
    const seasonalityMap = {
      '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
      '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
      '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
    };
    
    // Calculate the latest month with data
    let latestMonth = null;
    let latestMonthData = 0;
    
    for (const month in currentYearData) {
      if (currentYearData[month] > 0) {
        // Get the month part (MM) from YYYY-MM
        const monthPart = month.split('-')[1];
        if (!latestMonth || monthOrder.indexOf(monthPart) > monthOrder.indexOf(latestMonth)) {
          latestMonth = monthPart;
          latestMonthData = currentYearData[month];
        }
      }
    }
    
    // Calculate current year total
    const currentTotal = Object.values(currentYearData).reduce((sum, val) => sum + (val || 0), 0);
    
    // Calculate previous year total
    const previousTotal = Object.values(previousYearData).reduce((sum, val) => sum + (val || 0), 0);
    
    // Calculate predicted total using the seasonality data
    const seasonFraction = latestMonth ? seasonalityMap[latestMonth] || 0 : 0;
    const predictedTotal = seasonFraction > 0 ? Math.round(currentTotal / seasonFraction) : currentTotal;
    
    // Calculate percent change
    const percentChange = calculatePercentChange(predictedTotal, previousTotal);
    
    // Prepare CUMULATIVE chart data
    let currentYearCumulative = 0;
    let previousYearCumulative = 0;
    let predictedCumulative = 0;
    
    const chartDataPoints = monthOrder.map(monthNum => {
      // Extract year parts from energy year strings
      const currentEnergyYearParts = currentEnergyYear.split('-');
      const previousEnergyYearParts = previousEnergyYear.split('-');
      
      const currentStartYear = parseInt(currentEnergyYearParts[0]);
      const currentEndYear = parseInt(currentEnergyYearParts[1]);
      const previousStartYear = parseInt(previousEnergyYearParts[0]);
      const previousEndYear = parseInt(previousEnergyYearParts[1]);
      
      // Determine correct year for the month
      const currentYear = monthNum >= '07' ? currentStartYear : currentEndYear;
      const previousYear = monthNum >= '07' ? previousStartYear : previousEndYear;
      
      // Construct month strings
      const currentMonth = `${currentYear}-${monthNum}`;
      const previousMonth = `${previousYear}-${monthNum}`;
      
      // Get usage data
      const currentUsage = currentYearData[currentMonth] || 0;
      const previousUsage = previousYearData[previousMonth] || 0;
      
      // Add to cumulative totals
      currentYearCumulative += currentUsage;
      previousYearCumulative += previousUsage;
      
      // Calculate predicted value for this month
      if (currentUsage > 0) {
        predictedCumulative = currentYearCumulative;
      } 
      // For future months, use the predicted monthly values and add to cumulative
      else if (latestMonth) {
        // Get the month's position in the energy year
        const monthIndex = monthOrder.indexOf(monthNum);
        const latestMonthIndex = monthOrder.indexOf(latestMonth);
        
        // Only predict for months after the latest month with data
        if (monthIndex > latestMonthIndex) {
          // Get seasonality fractions
          const thisMonthFraction = seasonalityMap[monthNum];
          
          // Calculate the expected total by this month
          const expectedTotal = predictedTotal * thisMonthFraction;
          
          // Set the cumulative prediction
          predictedCumulative = expectedTotal;
        }
      }
      
      // Format month label
      const monthLabel = formatMonthLabel(currentMonth, windowWidth < 600);
      
      // Return data point
      return {
        month: monthLabel,
        rawMonth: currentMonth,
        currentYearCumulative: Math.round(currentYearCumulative),
        previousYearCumulative: Math.round(previousYearCumulative),
        predictedCumulative: predictedCumulative > 0 ? Math.round(predictedCumulative) : null,
        // Keep original monthly values for the tooltip
        currentYear: currentUsage,
        previousYear: previousUsage,
      };
    });
    
    // Set chart data
    setChartData(chartDataPoints);
    setTotalComparison({
      current: currentTotal,
      previous: previousTotal,
      predicted: predictedTotal,
      percentChange,
      efficiencyChange: efficiencyData?.percentChange || 0,
      latestMonth: latestMonth ? formatMonthLabel(`2023-${latestMonth}`, false) : null
    });
  };
  
  // Adjust month format based on screen size
  const formatMonthLabel = (monthStr, useShortFormat = false) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    
    // For smaller screens, use first letter only
    if (useShortFormat) {
      // Just return first letter of month name
      return date.toLocaleString('nl-NL', { month: 'short' }).charAt(0);
    }
    
    return date.toLocaleString('nl-NL', { month: 'short' });
  };
  
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(Math.round(num));
  };
  
  const renderTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const data = props.payload[0].payload;
    
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{data.month}</p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Dit jaar (cumulatief):</span> {formatNumber(data.currentYearCumulative)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Vorig jaar (cumulatief):</span> {formatNumber(data.previousYearCumulative)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Dit jaar (maand):</span> {formatNumber(data.currentYear)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Vorig jaar (maand):</span> {formatNumber(data.previousYear)} m³
        </p>
        {data.predictedCumulative && data.currentYearCumulative !== data.predictedCumulative && (
          <p className="tooltip-value">
            <span className="tooltip-label-inner">Voorspelling (cumulatief):</span> {formatNumber(data.predictedCumulative)} m³
          </p>
        )}
      </div>
    );
  };
  
  // Determine chart height based on screen width
  const getChartHeight = () => {
    if (windowWidth < 400) return 200; // Increased from 180
    if (windowWidth < 600) return 240; // Increased from 220
    return 280; // Increased from 250
  };
  
  // Determine if we should hide the legend based on screen width
  const shouldShowLegend = windowWidth >= 500;
  
  // Determine if we should use a horizontal layout for totals based on screen width
  const useHorizontalLayout = windowWidth >= 768;
  
  const handleSync = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Sync Kenter data
      await api.get('/api/kenter/sync');
      
      // Refresh data
      await fetchData();
      
    } catch (err) {
      console.error('❌ Error syncing data:', err);
      setError('Synchronisatie mislukt. Probeer het later nog eens.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="energy-year-comparison-widget card">
        <h3>📊 Energieverbruik per Maand</h3>
        <div className="tiny-loading-indicator">
  <div className="micro-spinner"></div>
  <span>Laden...</span>
</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="energy-year-comparison-widget card">
        <h3>📊 Energieverbruik per Maand</h3>
        <div className="error-message">
          <p>{error}</p>
          <button className="sync-button" onClick={handleSync}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="energy-year-comparison-widget card">
      <div className="widget-header">
        <h3>📊 Energieverbruik per Maand</h3>
      </div>
      
      {lastUpdate && (
        <div className="last-update">
          Laatste update: {lastUpdate}
        </div>
      )}
      
      <div className="chart-container2">
        <ResponsiveContainer width="100%" height={getChartHeight()}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: windowWidth < 500 ? 10 : 12 }}
              tickMargin={8}
            />
            <YAxis 
              tick={{ fontSize: windowWidth < 500 ? 10 : 12 }}
              width={windowWidth < 400 ? 30 : 40} // Increased width for better display
            />
            <Tooltip content={renderTooltip} />
            {shouldShowLegend && <Legend wrapperStyle={{ fontSize: windowWidth < 500 ? 10 : 12 }} />}
            
            {/* Current Year Area */}
            <Area
              type="monotone"
              dataKey="currentYearCumulative"
              name={`Dit jaar (${currentEnergyYear})`}
              fill="#FBBF24"
              fillOpacity={0.3}
              stroke="#FBBF24"
              strokeWidth={2}
              activeDot={{ r: windowWidth < 500 ? 4 : 6 }}
            />
            
            {/* Previous Year Line */}
            <Line
              type="monotone"
              dataKey="previousYearCumulative"
              name={`Vorig jaar (${previousEnergyYear})`}
              stroke="#4E8DF7"
              strokeWidth={2}
              dot={{ r: windowWidth < 500 ? 3 : 4 }}
              activeDot={{ r: windowWidth < 500 ? 5 : 6 }}
            />
            
            {/* Prediction Line */}
            <Line
              type="monotone"
              dataKey="predictedCumulative"
              name="Voorspelling"
              stroke="#000000"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              activeDot={{ r: windowWidth < 500 ? 4 : 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {!shouldShowLegend && (
        <div className="compact-legend">
          <span className="legend-item"><span className="color-box current"></span> Dit jaar</span>
          <span className="legend-item"><span className="color-box previous"></span> Vorig jaar</span>
          <span className="legend-item"><span className="color-box prediction"></span> Voorspelling</span>
        </div>
      )}
      
      {totalComparison && (
        <div className={`total-comparison ${useHorizontalLayout ? 'horizontal' : 'vertical'}`}>
          <div className="total-metric">
            <h4>Totaal dit jaar</h4>
            <span className="metric-value">{formatNumber(totalComparison.current)} m³</span>
            {totalComparison.latestMonth && (
              <div className="metric-note">t/m {totalComparison.latestMonth}</div>
            )}
          </div>
          <div className="total-metric">
            <h4>Voorspelling</h4>
            <span className="metric-value prediction">{formatNumber(totalComparison.predicted)} m³</span>
          </div>
          <div className="total-metric">
            <h4>Totaal vorig jaar</h4>
            <span className="metric-value">{formatNumber(totalComparison.previous)} m³</span>
          </div>
          <div className="total-metric change">
            <h4>Verandering</h4>
            <span className={`metric-value ${parseFloat(totalComparison.percentChange) < 0 ? 'negative-change' : 'positive-change'}`}>
              {totalComparison.percentChange}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetEnergyCompare;